import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

import { environment } from '../../environments/environment';

/**
 * Live "the project list changed" push, over the existing SignalR hub.
 *
 * WHY THIS EXISTS: the project list is FETCH-ONCE. `loadProjectList` has three
 * callers — app init, switch-customer, and a retry button shown only after a
 * failure. A cure started on the desktop does not appear until someone reloads.
 *
 * THE DESIGN, and the first point is the one that matters:
 *
 * 1. NOTIFICATION, NOT DATA. The server sends only that something changed; this
 *    client then refetches through GetProjectlist, which already enforces
 *    authorization. The hub never carries project data, so it cannot leak any —
 *    and there is one place scoping is enforced rather than two.
 *
 * 2. SCOPED BY CUSTOMER GROUP, JOINED FROM THE VALIDATED TOKEN. `subscribe-
 *    customer` takes NO customer parameter; the server resolves the caller's
 *    company from the bearer. That absence is the security property. This hub has
 *    already leaked cross-customer once — before 2026-08-26 it broadcast raw
 *    sample frames for every project to every connected client — and a
 *    project-created event has no natural project boundary, so the naive version
 *    reproduces exactly that incident.
 *
 * 3. AN OPTIMISATION, NOT A SOURCE OF TRUTH. Every signal is a hint to refetch. A
 *    dropped connection, a missed frame or a disabled flag cost freshness and
 *    nothing else: the list is still correct on the next load. That is also what
 *    makes it safe to demo — if the socket misbehaves, a refresh still works.
 *
 * SEPARATE CONNECTION FROM LiveTelemetryService, deliberately. That one is keyed
 * to a project: `start(projectName)` refuses without one and `stop()` runs on
 * deselect. This one must live while NO project is selected, which is precisely
 * when the user is looking at the list. Its sibling's comments record "~62 sockets
 * in 45 minutes" from getting one connection's lifecycle wrong; giving a second
 * concern to that same object is how that happens again.
 */

export interface ProjectChangedMessage {
  projectName?: string;
  customerId?: number | string;
  reason?: string;
}

interface PushConfig {
  negotiateRoute: string;
  subscribeRoute: string;
  /**
   * The hub event name.
   *
   * A CONSTANT IN ONE PLACE because it is the highest-risk string in this file. A
   * wrong value fails silently and completely: the socket connects, subscribe
   * returns 200, the group is joined, and no frame ever arrives — nothing throws
   * and nothing logs an error. This project has shipped exactly that shape before
   * (a resolver sending `?imageKey=` to a route reading `?key=`, inert for two
   * deploys). Overridable from environment so it can be corrected without a code
   * change if the server's name differs.
   */
  changedEvent: string;
}

const DEFAULT_PUSH_CONFIG: PushConfig = {
  negotiateRoute: 'live/negotiate',
  subscribeRoute: 'live/subscribe-customer',
  changedEvent: 'projectChanged',
};

export function projectListPushEnabled(): boolean {
  return (
    (environment as { liveProjectListPushEnabled?: boolean })
      .liveProjectListPushEnabled === true
  );
}

export function projectListPushConfig(): PushConfig {
  const configured = (environment as { projectListPush?: Partial<PushConfig> })
    .projectListPush;
  return { ...DEFAULT_PUSH_CONFIG, ...(configured ?? {}) };
}

/**
 * Channel state, EXPORTED so the shell can render it. A socket that dies quietly
 * is indistinguishable from a tenant where nothing has happened — beta-dev ran a
 * fourteen-day silent outage on exactly that ambiguity.
 */
export type PushState = 'disabled' | 'idle' | 'connecting' | 'live' | 'failed';

@Injectable({ providedIn: 'root' })
export class ProjectListPushService {
  private connection: HubConnection | null = null;
  private starting: Promise<void> | null = null;

  private readonly changedSource = new Subject<ProjectChangedMessage>();
  /**
   * Fires when the list may have changed. Subscribers should REFETCH, not trust
   * the payload — see point 1 above.
   */
  readonly changed$: Observable<ProjectChangedMessage> = this.changedSource.asObservable();

  private readonly stateSource = new BehaviorSubject<PushState>('idle');
  readonly state$: Observable<PushState> = this.stateSource.asObservable();
  get state(): PushState {
    return this.stateSource.value;
  }

  /** Echoed by the server so both sides can be diffed from one browser capture. */
  lastGroup: string | null = null;
  lastConnectionId: string | null = null;

  constructor(
    private http: HttpClient,
    private zone: NgZone
  ) {}

  /**
   * Connect and join this caller's customer group. Idempotent.
   *
   * ONE CONNECTION, HELD — the same rule its sibling learned the hard way. The
   * in-flight promise is returned to concurrent callers so two navigations cannot
   * each build a socket.
   */
  async start(): Promise<void> {
    if (!projectListPushEnabled()) {
      this.setState('disabled');
      return;
    }
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }
    if (this.starting) {
      return this.starting;
    }
    this.starting = this.connect().finally(() => {
      this.starting = null;
    });
    return this.starting;
  }

  private async connect(): Promise<void> {
    this.setState('connecting');
    try {
      // NEGOTIATE WITH NO PROJECT. Confirmed with DataServices: the route takes no
      // project at all, which is what lets this connection exist on the
      // project-selection screen where none is selected.
      const negotiated = await firstValueFrom(
        this.http.post<{ url: string; accessToken: string }>(
          this.url(projectListPushConfig().negotiateRoute),
          {}
        )
      );
      if (!negotiated?.url || !negotiated?.accessToken) {
        throw new Error('negotiate returned no url/accessToken');
      }

      const connection = new HubConnectionBuilder()
        .withUrl(negotiated.url, {
          accessTokenFactory: () => negotiated.accessToken,
          transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      connection.on(projectListPushConfig().changedEvent, (message: ProjectChangedMessage) =>
        // SignalR callbacks land outside Angular's zone; re-enter or the grid does
        // not repaint until something else happens to trigger change detection.
        this.zone.run(() => this.changedSource.next(message ?? {}))
      );

      connection.onreconnected(() => {
        // RE-JOIN AND CATCH UP. The group is bound to a connection id, and a
        // reconnect issues a new one — so without re-subscribing the socket is
        // live and silent, which is indistinguishable from "nothing changed".
        // The catch-up emission covers anything missed while it was down; a
        // duplicate refetch is harmless because the refetch REPLACES the list.
        void this.subscribe().then(() => this.zone.run(() => this.changedSource.next({ reason: 'reconnected' })));
      });
      connection.onclose(() => this.setState('failed'));

      await connection.start();
      this.connection = connection;

      const joined = await this.subscribe();
      this.setState(joined ? 'live' : 'failed');
    } catch (err) {
      // FAIL QUIET. This is an optimisation; a customer whose company cannot be
      // resolved is refused by design, and a refused push must not break the list.
      console.warn('ProjectListPush: could not start', err);
      this.setState('failed');
    }
  }

  /**
   * Join the caller's customer group.
   *
   * SENDS NO CUSTOMER ID, and that is the point. The server resolves the company
   * from the validated bearer; a client-supplied id would be the GetCustomers IDOR
   * shape all over again — identity taken from the request instead of the token.
   */
  private async subscribe(): Promise<boolean> {
    const id = this.connection?.connectionId;
    if (!id) {
      return false;
    }
    try {
      const url =
        `${this.url(projectListPushConfig().subscribeRoute)}` +
        `?connectionId=${encodeURIComponent(id)}`;
      const body = (await firstValueFrom(this.http.post(url, {}))) as { group?: string } | null;
      this.lastGroup = body?.group ?? '(not echoed)';
      this.lastConnectionId = id;
      console.log(
        `ProjectListPush: joined group=${this.lastGroup} connectionId=${id}`
      );
      return true;
    } catch (err) {
      console.warn('ProjectListPush: subscribe-customer refused', err);
      return false;
    }
  }

  async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    if (!connection) {
      return;
    }
    try {
      await connection.stop();
    } catch {
      // a socket that is already gone must not throw on the way out
    }
    this.setState('idle');
  }

  private url(route: string): string {
    const base = environment.apiUrl.endsWith('/')
      ? environment.apiUrl
      : `${environment.apiUrl}/`;
    return `${base}${route.replace(/^\/+/, '')}`;
  }

  private setState(state: PushState): void {
    if (this.stateSource.value !== state) {
      this.zone.run(() => this.stateSource.next(state));
    }
  }
}
